import React, { useState, useEffect } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import Select from "react-select";

const FIELD_SECTIONS = [
  {
    title: "Identification",
    fields: [
      { key: "asset_id", label: "Asset ID" },
      { key: "asset_type", label: "Asset Type", placeholder: "Laptop / Desktop / Printer / Server" },
      { key: "brand", label: "Brand", placeholder: "Dell / HP / Lenovo" },
      { key: "model", label: "Model" },
      { key: "serial_number", label: "Serial Number" },
    ],
  },
  {
    title: "Dates & Financials",
    fields: [
      { key: "purchase_date", label: "Purchase Date" },
      { key: "installation_date", label: "Installation / Deployment Date" },
      { key: "purchase_cost", label: "Purchase Cost" },
      { key: "warranty_start", label: "Warranty Start" },
      { key: "warranty_end", label: "Warranty End" },
      { key: "asset_age", label: "Asset Age" },
      { key: "expected_life", label: "Expected Life" },
      { key: "replacement_due", label: "Replacement Due" },
    ],
  },
  {
    title: "Status & Assignment",
    fields: [
      { key: "current_status", label: "Current Status", placeholder: "Active / Spare / Repair / Lost" },
      { key: "condition", label: "Condition", placeholder: "Excellent / Good / Fair / Poor" },
      { key: "assigned_employee", label: "Assigned Employee" },
      { key: "location", label: "Location" },
    ],
  },
  {
    title: "Network & IT",
    fields: [
      { key: "username", label: "Username" },
      { key: "ip_address", label: "IP Address" },
      { key: "mac_address", label: "MAC Address" },
      { key: "wifi", label: "Wi-Fi", placeholder: "Yes / No" },
      { key: "os", label: "OS" },
      { key: "windows_license", label: "Windows License" },
    ],
  },
  {
    title: "Software & Security",
    fields: [
      { key: "antivirus", label: "Antivirus" },
      { key: "antivirus_expiry", label: "Antivirus Expiry" },
      { key: "erp_id", label: "ERP ID" },
      { key: "email_id", label: "Email ID" },
    ],
  },
  {
    title: "Maintenance",
    fields: [
      { key: "last_service_date", label: "Last Service Date" },
      { key: "next_service_due", label: "Next Service Due" },
    ],
  },
];

const stringKeys = FIELD_SECTIONS.flatMap((s) => s.fields.map((f) => f.key)).concat([
  "remarks",
  "department",
]);

const emptyForm = () => {
  const base = {
    asset_item_master_id: "",
    asset_department_id: "",
  };
  stringKeys.forEach((k) => {
    base[k] = "";
  });
  return base;
};

const AssetList = () => {
  const { http } = AuthUser();
  const [rows, setRows] = useState([]);
  const [itemMasters, setItemMasters] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState(emptyForm());
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedDept, setSelectedDept] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchRows();
    fetchMasters();
    fetchDepartments();
  }, []);

  const fetchRows = async () => {
    try {
      const response = await http.get("/assets");
      if (response.data.success) {
        setRows(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch (error) {
      toast.error("Error loading assets");
    }
  };

  const fetchMasters = async () => {
    try {
      const response = await http.get("/asset_item_masters");
      if (response.data.success) setItemMasters(response.data.data || []);
    } catch (error) {
      toast.error("Error loading item masters");
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await http.get("/asset_departments");
      if (response.data.success) setDepartments(response.data.data || []);
    } catch (error) {
      toast.error("Error loading departments");
    }
  };

  const itemOptions = itemMasters.map((m) => ({
    value: m.id,
    label: m.head?.name ? `${m.name} (${m.head.name})` : m.name,
  }));

  const deptOptions = departments.map((d) => ({
    value: d.id,
    label: d.name,
  }));

  const handleField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const openAdd = () => {
    setEditing(null);
    setFormData(emptyForm());
    setSelectedItem(null);
    setSelectedDept(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setEditing(row);
    const next = emptyForm();
    Object.keys(next).forEach((k) => {
      if (row[k] != null) next[k] = String(row[k]);
    });
    setFormData(next);
    setSelectedItem(
      row.item_master
        ? {
            value: row.item_master.id,
            label: row.item_master.head?.name
              ? `${row.item_master.name} (${row.item_master.head.name})`
              : row.item_master.name,
          }
        : itemOptions.find((o) => o.value === row.asset_item_master_id) || null
    );
    setSelectedDept(
      row.asset_department
        ? { value: row.asset_department.id, label: row.asset_department.name }
        : deptOptions.find((o) => o.value === row.asset_department_id) || null
    );
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    try {
      await http.delete(`/assets/${id}`);
      toast.success("Asset deleted");
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error deleting");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.asset_item_master_id) {
      toast.error("Please select an Item Master");
      return;
    }
    setIsLoading(true);
    try {
      const payload = { ...formData };
      if (!payload.asset_department_id) payload.asset_department_id = null;
      let response;
      if (editing) {
        response = await http.put(`/assets/${editing.id}`, payload);
        toast.success("Asset updated successfully");
      } else {
        response = await http.post("/assets", payload);
        toast.success("Asset created successfully");
      }
      setShowModal(false);
      setEditing(null);
      setFormData(emptyForm());
      setSelectedItem(null);
      setSelectedDept(null);
      setSearchTerm("");
      // Prefer list refresh; fall back to merging saved row so UI updates immediately
      try {
        const list = await http.get("/assets");
        if (list.data.success && Array.isArray(list.data.data)) {
          setRows(list.data.data);
        } else if (response?.data?.data) {
          const saved = response.data.data;
          setRows((prev) => {
            const others = prev.filter((r) => r.id !== saved.id);
            return [saved, ...others];
          });
        }
      } catch (_) {
        if (response?.data?.data) {
          const saved = response.data.data;
          setRows((prev) => {
            const others = prev.filter((r) => r.id !== saved.id);
            return [saved, ...others];
          });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving asset");
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = rows.filter((r) => {
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return true;
    return (
      (r.asset_id || "").toLowerCase().includes(q) ||
      (r.serial_number || "").toLowerCase().includes(q) ||
      (r.brand || "").toLowerCase().includes(q) ||
      (r.model || "").toLowerCase().includes(q) ||
      (r.assigned_employee || "").toLowerCase().includes(q) ||
      (r.department || "").toLowerCase().includes(q) ||
      (r.item_master?.name || "").toLowerCase().includes(q) ||
      (r.asset_type || "").toLowerCase().includes(q) ||
      (r.current_status || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="container-fluid mt-4">
      <Toaster />
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Asset List</h5>
          <button className="btn btn-sm btn-primary" onClick={openAdd}>
            <i className="fas fa-plus-circle me-1"></i> Add Asset
          </button>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search by Asset ID, item master, serial, brand, employee..."
              value={searchTerm}
              autoComplete="off"
              name="asset_list_search"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-bordered table-sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Asset ID</th>
                  <th>Item Master</th>
                  <th>Type</th>
                  <th>Brand / Model</th>
                  <th>Serial</th>
                  <th>Status</th>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? (
                  filtered.map((row) => (
                    <tr key={row.id}>
                      <td>{row.id}</td>
                      <td>{row.asset_id || "-"}</td>
                      <td>{row.item_master?.name || "-"}</td>
                      <td>{row.asset_type || "-"}</td>
                      <td>
                        {[row.brand, row.model].filter(Boolean).join(" / ") || "-"}
                      </td>
                      <td>{row.serial_number || "-"}</td>
                      <td>{row.current_status || "-"}</td>
                      <td>{row.assigned_employee || "-"}</td>
                      <td>
                        {row.asset_department?.name || row.department || "-"}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button
                          className="btn btn-sm btn-warning me-1"
                          onClick={() => handleEdit(row)}
                          title="Edit"
                        >
                          <i className="fas fa-pen"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(row.id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center">
                      No assets found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="modal show d-block asset-modal-overlay"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl asset-modal-dialog">
            <div className="modal-content asset-modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editing ? "Edit Asset" : "Add Asset"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit} className="asset-modal-form" autoComplete="off">
                <div className="modal-body asset-modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">
                        Item Master <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={itemOptions}
                        value={selectedItem}
                        onChange={(opt) => {
                          setSelectedItem(opt);
                          handleField(
                            "asset_item_master_id",
                            opt ? opt.value : ""
                          );
                        }}
                        placeholder="Search item master..."
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Department</label>
                      <Select
                        options={deptOptions}
                        value={selectedDept}
                        onChange={(opt) => {
                          setSelectedDept(opt);
                          handleField(
                            "asset_department_id",
                            opt ? opt.value : ""
                          );
                          handleField("department", opt ? opt.label : "");
                        }}
                        placeholder="Search department..."
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>
                  </div>

                  {FIELD_SECTIONS.map((section) => (
                    <div key={section.title} className="mb-3">
                      <h6 className="border-bottom pb-1 text-secondary">
                        {section.title}
                      </h6>
                      <div className="row">
                        {section.fields.map((f) => (
                          <div className="col-md-4 mb-2" key={f.key}>
                            <label className="form-label small mb-1">
                              {f.label}
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder={f.placeholder || ""}
                              value={formData[f.key] || ""}
                              onChange={(e) =>
                                handleField(f.key, e.target.value)
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="mb-2">
                    <label className="form-label">Remarks</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={formData.remarks || ""}
                      onChange={(e) => handleField("remarks", e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .asset-modal-overlay {
          overflow: hidden !important;
        }
        .asset-modal-dialog {
          margin: 1rem auto;
          max-width: 1100px;
          height: calc(100vh - 2rem);
          max-height: calc(100vh - 2rem);
        }
        .asset-modal-content {
          height: 100%;
          max-height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .asset-modal-form {
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0;
          overflow: hidden;
        }
        .asset-modal-content .modal-header,
        .asset-modal-form .modal-footer {
          flex: 0 0 auto;
        }
        .asset-modal-body {
          flex: 1 1 auto;
          overflow-y: auto !important;
          min-height: 0;
          -webkit-overflow-scrolling: touch;
        }
      `}</style>
    </div>
  );
};

export default AssetList;
